import { useState, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { Skeleton } from "@/components/ui/Skeleton";
import { SidePanelBody } from "@/components/ui/SidePanel";
import { useToast } from "@/components/ui/Toast";
import {
  useFetchClubInvitationsQuery,
  useUpdateClubInvitationMutation,
  useSendClubInvitationsMutation,
} from "@/api/clubsApi";
import type { ClubInvitation } from "@/api/clubsApi";

interface InvitationDashboardProps {
  clubId: string;
}

type FilterTab = "all" | "pending" | "sent" | "accepted";

const filterTabs: { key: FilterTab; label: string }[] = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "sent", label: "Sent" },
  { key: "accepted", label: "Accepted" },
];

export function InvitationDashboard({ clubId }: InvitationDashboardProps) {
  const [activeFilter, setActiveFilter] = useState<FilterTab>("all");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editEmail, setEditEmail] = useState("");
  const { showToast } = useToast();

  const { data: invitations = [], isLoading } = useFetchClubInvitationsQuery({ clubId });
  const [updateInvitation] = useUpdateClubInvitationMutation();
  const [sendInvitations, { isLoading: isSending }] = useSendClubInvitationsMutation();

  const filteredInvitations = useMemo(() => {
    if (activeFilter === "all") return invitations;
    return invitations.filter((inv) => inv.status === activeFilter);
  }, [invitations, activeFilter]);

  const pendingWithEmail = useMemo(
    () => invitations.filter((inv) => inv.status === "pending" && inv.email),
    [invitations]
  );

  const handleSendAll = useCallback(async () => {
    if (pendingWithEmail.length === 0) return;

    try {
      const result = await sendInvitations({
        clubId,
        invitationIds: pendingWithEmail.map((inv) => inv.id),
      }).unwrap();

      if (result.sent > 0) {
        showToast(`${result.sent} invitation${result.sent !== 1 ? "s" : ""} sent`, "success");
      }
      if (result.failed > 0) {
        showToast(`${result.failed} failed to send`, "error");
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to send invitations", "error");
    }
  }, [pendingWithEmail, clubId, sendInvitations, showToast]);

  const handleSendOne = useCallback(
    async (invitationId: string) => {
      try {
        const result = await sendInvitations({
          clubId,
          invitationIds: [invitationId],
        }).unwrap();

        if (result.sent > 0) {
          showToast("Invitation sent", "success");
        } else {
          showToast("Failed to send invitation", "error");
        }
      } catch (err) {
        showToast(err instanceof Error ? err.message : "Failed to send", "error");
      }
    },
    [clubId, sendInvitations, showToast]
  );

  const handleRevoke = useCallback(
    async (invitationId: string) => {
      try {
        await updateInvitation({
          id: invitationId,
          updates: { status: "revoked" },
        }).unwrap();
        showToast("Invitation revoked", "success");
      } catch (err) {
        showToast(err instanceof Error ? err.message : "Failed to revoke", "error");
      }
    },
    [updateInvitation, showToast]
  );

  const handleStartEditEmail = useCallback((invitation: ClubInvitation) => {
    setEditingId(invitation.id);
    setEditEmail(invitation.email || "");
  }, []);

  const handleSaveEmail = useCallback(
    async (invitationId: string) => {
      try {
        await updateInvitation({
          id: invitationId,
          updates: { email: editEmail.trim() || undefined },
        }).unwrap();
        setEditingId(null);
        showToast("Email updated", "success");
      } catch (err) {
        showToast(err instanceof Error ? err.message : "Failed to update email", "error");
      }
    },
    [editEmail, updateInvitation, showToast]
  );

  const counts = useMemo(() => {
    const all = invitations.length;
    const pending = invitations.filter((i) => i.status === "pending").length;
    const sent = invitations.filter((i) => i.status === "sent").length;
    const accepted = invitations.filter((i) => i.status === "accepted").length;
    return { all, pending, sent, accepted };
  }, [invitations]);

  if (isLoading) {
    return (
      <SidePanelBody className="pt-0">
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-14 w-full rounded-xl" />
          ))}
        </div>
      </SidePanelBody>
    );
  }

  return (
    <SidePanelBody className="pt-0">
      <div className="space-y-4 pb-4">
        {/* Filter Tabs */}
        <div className="flex gap-1 p-1 bg-surface-1 rounded-xl">
          {filterTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveFilter(tab.key)}
              className={`flex-1 px-3 py-1.5 rounded-lg text-body-3-medium transition-colors ${
                activeFilter === tab.key
                  ? "bg-background text-text-primary shadow-sm"
                  : "text-text-secondary hover:text-text-primary"
              }`}
            >
              {tab.label}
              <span className="ml-1 text-text-tertiary">
                {counts[tab.key]}
              </span>
            </button>
          ))}
        </div>

        {/* Send All Button */}
        {pendingWithEmail.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={handleSendAll}
            disabled={isSending}
          >
            <Icon name="paper-plane" size={14} />
            {isSending
              ? "Sending..."
              : `Send ${pendingWithEmail.length} pending invitation${pendingWithEmail.length !== 1 ? "s" : ""}`}
          </Button>
        )}

        {/* Invitation List */}
        {filteredInvitations.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="envelope-open-text" size={32} className="text-text-tertiary mx-auto mb-3" />
            <p className="text-body-2 text-text-secondary">
              {activeFilter === "all" ? "No invitations yet" : `No ${activeFilter} invitations`}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredInvitations.map((invitation) => (
              <InvitationRow
                key={invitation.id}
                invitation={invitation}
                isEditing={editingId === invitation.id}
                editEmail={editEmail}
                onEditEmailChange={setEditEmail}
                onStartEdit={() => handleStartEditEmail(invitation)}
                onSaveEmail={() => handleSaveEmail(invitation.id)}
                onCancelEdit={() => setEditingId(null)}
                onSend={() => handleSendOne(invitation.id)}
                onRevoke={() => handleRevoke(invitation.id)}
                isSending={isSending}
              />
            ))}
          </div>
        )}
      </div>
    </SidePanelBody>
  );
}

function InvitationRow({
  invitation,
  isEditing,
  editEmail,
  onEditEmailChange,
  onStartEdit,
  onSaveEmail,
  onCancelEdit,
  onSend,
  onRevoke,
  isSending,
}: {
  invitation: ClubInvitation;
  isEditing: boolean;
  editEmail: string;
  onEditEmailChange: (email: string) => void;
  onStartEdit: () => void;
  onSaveEmail: () => void;
  onCancelEdit: () => void;
  onSend: () => void;
  onRevoke: () => void;
  isSending: boolean;
}) {
  return (
    <div className="bg-surface rounded-xl p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-full bg-surface-1 flex items-center justify-center flex-shrink-0">
            <Icon name="user" size={14} className="text-text-tertiary" />
          </div>
          <div className="min-w-0">
            <p className="text-body-2-medium text-text-primary truncate">{invitation.name}</p>
            {isEditing ? (
              <div className="flex items-center gap-1 mt-0.5">
                <input
                  className="text-body-3 bg-transparent border border-accent-primary rounded px-1.5 py-0.5 text-text-primary focus:outline-none w-[180px]"
                  value={editEmail}
                  onChange={(e) => onEditEmailChange(e.target.value)}
                  placeholder="email@example.com"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onSaveEmail();
                    if (e.key === "Escape") onCancelEdit();
                  }}
                />
                <Button variant="ghost" size="sm" onClick={onSaveEmail} className="h-6 px-2">
                  <Icon name="check" size={10} />
                </Button>
                <Button variant="ghost" size="sm" onClick={onCancelEdit} className="h-6 px-2">
                  <Icon name="xmark" size={10} />
                </Button>
              </div>
            ) : (
              <p className="text-body-3 text-text-secondary truncate">
                {invitation.email || (
                  <button onClick={onStartEdit} className="text-accent-primary hover:underline">
                    Add email
                  </button>
                )}
              </p>
            )}
          </div>
        </div>

        <StatusBadge status={invitation.status} />
      </div>

      {/* Actions */}
      {(invitation.status === "pending" || invitation.status === "sent") && (
        <div className="flex items-center gap-2 pl-10">
          {invitation.status === "pending" && invitation.email && (
            <Button variant="ghost" size="sm" onClick={onSend} disabled={isSending} className="h-7 text-body-3">
              <Icon name="paper-plane" size={10} />
              Send
            </Button>
          )}
          {invitation.status === "pending" && !invitation.email && !isEditing && (
            <Button variant="ghost" size="sm" onClick={onStartEdit} className="h-7 text-body-3">
              <Icon name="pen" size={10} />
              Add email
            </Button>
          )}
          {invitation.status === "sent" && (
            <Button variant="ghost" size="sm" onClick={onSend} disabled={isSending} className="h-7 text-body-3">
              <Icon name="arrow-rotate-right" size={10} />
              Resend
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onRevoke}
            className="h-7 text-body-3 text-accent-negative hover:text-accent-negative"
          >
            <Icon name="xmark" size={10} />
            Revoke
          </Button>
        </div>
      )}

      {/* Source & Date */}
      <div className="flex items-center gap-3 pl-10 text-body-3 text-text-tertiary">
        <span>{formatSource(invitation.source)}</span>
        <span>{formatDate(invitation.created_at)}</span>
        {invitation.expires_at && invitation.status !== "accepted" && (
          <span>Expires {formatDate(invitation.expires_at)}</span>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; className: string }> = {
    pending: { label: "Pending", className: "bg-amber-500/10 text-amber-600" },
    sent: { label: "Sent", className: "bg-blue-500/10 text-blue-500" },
    accepted: { label: "Accepted", className: "bg-green-500/10 text-green-500" },
    expired: { label: "Expired", className: "bg-surface-1 text-text-tertiary" },
    revoked: { label: "Revoked", className: "bg-accent-negative/10 text-accent-negative" },
  };

  const { label, className } = config[status] || { label: status, className: "bg-surface-1 text-text-tertiary" };

  return (
    <span className={`px-2 py-0.5 rounded-full text-body-3-medium ${className}`}>
      {label}
    </span>
  );
}

function formatSource(source: string): string {
  switch (source) {
    case "meetup_import":
      return "Meetup import";
    case "screenshot_import":
      return "Screenshot import";
    case "manual":
      return "Manual";
    default:
      return source;
  }
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
